import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from "date-fns/locale";
import { ThumbsUp, MessageCircle, Share2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { auth, db } from '../firebase'; // đường dẫn firebase config của bạn
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import PhoneLoginPopup from './PhoneLoginPopup';
import { useNavigate } from "react-router-dom";
import SharePopup from "./SharePopup";

export default function Post({ post }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeDocId, setLikeDocId] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [isLong, setIsLong] = useState(false);
  const currentUser = auth.currentUser;
  const timeAgo = formatDistanceToNow(new Date(post.createdDate), {
    addSuffix: true,
    locale: vi,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Check nếu scrollHeight lớn hơn height hiển thị (tức là bị clamp)
    if (el.scrollHeight > el.clientHeight) {
      setIsLong(true);
    }
  }, []);

  useEffect(() => {
    async function fetchPreview() {
      if (post.linkContent) {
        try {
          const res = await fetch(
            `https://getlinkpreview-vbnbnaa5aq-uc.a.run.app?url=${encodeURIComponent(post.linkContent)}`
          );
          const data = await res.json();
          if (!data.error) setLinkPreview(data);
        } catch (err) {
          console.error("Failed to fetch link preview:", err);
        }
      }
    }
    fetchPreview();
  }, [post.linkContent]);

  useEffect(() => {
    if (!currentUser) {
      setLiked(false);
      setLikeDocId(null);
      return;
    }
    const checkLike = async () => {
      const q = query(
        collection(db, "likes"),
        where("postId", "==", post.id),
        where("userId", "==", currentUser.uid)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setLiked(true);
        setLikeDocId(snapshot.docs[0].id);
      } else {
        setLiked(false);
        setLikeDocId(null);
      }
    };
    checkLike();
  }, [currentUser, post.id]);

  const handleLikeClick = async () => {
    if (!currentUser) {
      setShowLoginPopup(true);
      return;
    }

    if (liked && likeDocId) {
      // Bỏ quan tâm
      await deleteDoc(doc(db, "likes", likeDocId));
      setLiked(false);
      setLikeDocId(null);
    } else {
      // Quan tâm
      const docRef = await addDoc(collection(db, "likes"), {
        createdDate: Date.now(),
        id: "", // sẽ cập nhật sau
        postId: post.id,
        userId: currentUser.uid
      });
      await updateDoc(docRef, { id: docRef.id }); // hoặc updateDoc(docRef, { id: docRef.id })
      setLiked(true);
      setLikeDocId(docRef.id);
    }
  };

  return (
    <div className="bg-white p-3 md:p-4 rounded shadow border border-halloween-orange/25 shadow-halloween-purple/10 text-sm md:text-base" onClick={() => navigate(`/post/${post.id}/0`)}>
      <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-2">{post.title}</h3>

      {post.textContent && (
        <div>
          <p ref={ref} className="mb-1 md:mb-2 whitespace-pre-line line-clamp-[10]">{post.textContent}</p>
          {isLong && (
            <a
              href={`/post/${post.id}/0`}
              className="text-vietnam-primary underline"
            >
              Xem thêm ⇒
            </a>
          )}
        </div>
      )}

      <MediaGrid mediaContent={post.mediaContent} />

      {linkPreview && (
        <a
          href={linkPreview.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block border rounded-lg overflow-hidden mt-3"
        >
          {linkPreview.image && (
            <img
              src={linkPreview.image}
              alt={linkPreview.title}
              className="w-full object-cover h-48"
            />
          )}
          <div className="p-3">
            <h4 className="font-semibold">{linkPreview.title}</h4>
            <p className="text-sm text-gray-600 line-clamp-2">
              {linkPreview.description}
            </p>
          </div>
        </a>
      )}

      <p className="text-gray-500 text-xs md:text-sm mt-2">
        {'Admin đã đăng ' + timeAgo}
      </p>

      <div className="mt-3 md:mt-4 flex space-x-1 md:space-x-2 text-xs md:text-sm">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleLikeClick();
          }}
          className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 ${liked ? "bg-vietnam-light text-vietnam-primary" : "hover:bg-red-50"
            }`}
        >
          <ThumbsUp className={`w-4 h-4 ${liked ? "text-vietnam-primary" : "text-vietnam-dark"}`} />
          <span>{liked ? "Đã quan tâm" : "Quan tâm"}</span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/post/${post.id}/1`);
          }}
          className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors duration-200">
          <MessageCircle className="w-4 h-4 text-vietnam-primary" />
          <span className="text-gray-700">Bình luận</span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowShare(true);
          }}
          className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors duration-200">
          <Share2 className="w-4 h-4 text-vietnam-primary" />
          <span className="text-gray-700">Chia sẻ</span>
        </button>
      </div>

      {showLoginPopup && (
        <PhoneLoginPopup onClose={() => setShowLoginPopup(false)} />
      )}
      {showShare && (
        <SharePopup
          postId={post.id}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}

function MediaGrid({ mediaContent }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  if (!Array.isArray(mediaContent) || mediaContent.length === 0) return null;

  const count = mediaContent.length;

  const renderMedia = (media, index, clickable = true) => {
    const commonProps = {
      key: index,
      onClick: clickable ? () => setSelectedIndex(index) : undefined,
      className:
        "w-full h-full object-cover rounded cursor-pointer hover:opacity-90 transition",
    };

    return media.isVideo ? (
      <video {...commonProps} controls={false}>
        <source src={media.url} type="video/mp4" />
      </video>
    ) : (
      <img {...commonProps} src={media.url} alt="media" />
    );
  };

  // --- GRID LAYOUT ---
  if (count === 1) {
    return <div className="rounded overflow-hidden">{renderMedia(mediaContent[0], 0)}</div>;
  }

  if (count === 2) {
    return <div className="grid grid-cols-2 gap-1">{mediaContent.map(renderMedia)}</div>;
  }

  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-1">
        <div className="col-span-1 row-span-2">{renderMedia(mediaContent[0], 0)}</div>
        <div>{renderMedia(mediaContent[1], 1)}</div>
        <div>{renderMedia(mediaContent[2], 2)}</div>
      </div>
    );
  }

  if (count === 4) {
    return <div className="grid grid-cols-2 gap-1">{mediaContent.slice(0, 4).map(renderMedia)}</div>;
  }

  // >4
  return (
    <div className="grid grid-cols-2 gap-1 relative">
      {mediaContent.slice(0, 4).map((m, i) => {
        if (i === 3) {
          return (
            <div key={i} className="relative cursor-pointer" onClick={() => setSelectedIndex(i)}>
              {renderMedia(m, i)}
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl font-bold rounded">
                +{count - 4}
              </div>
            </div>
          );
        }
        return renderMedia(m, i);
      })}

      {/* Lightbox */}
      {selectedIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <button
            onClick={() => setSelectedIndex(null)}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <X size={32} />
          </button>

          <button
            onClick={() =>
              setSelectedIndex((prev) => (prev > 0 ? prev - 1 : mediaContent.length - 1))
            }
            className="absolute left-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <ChevronLeft size={36} />
          </button>

          <div className="max-w-4xl max-h-[80vh]">
            {mediaContent[selectedIndex].isVideo ? (
              <video
                src={mediaContent[selectedIndex].url}
                controls
                autoPlay
                className="max-h-[80vh] rounded"
              />
            ) : (
              <img
                src={mediaContent[selectedIndex].url}
                alt="full"
                className="max-h-[80vh] rounded"
              />
            )}
          </div>

          <button
            onClick={() =>
              setSelectedIndex((prev) => (prev < mediaContent.length - 1 ? prev + 1 : 0))
            }
            className="absolute right-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </div>
  );
}